import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import StockPriceChart, { filterQuotesByPriceRange } from '../Components/StockPriceChart';
import { MARKET_RANGE_PRESETS } from '../Components/MarketComparisonChart';
import './Markets.css';

/** DB `markets.symbol` for index rows matches quotes service label: index key uppercased (e.g. tsx → TSX). */
function indexKeyToStoredSymbol(key) {
    return String(key).toUpperCase();
}

/** Title-case index key for display (e.g. russell2000 → Russell2000). */
function formatIndexTitle(key) {
    return String(key)
        .split('_')
        .map(part =>
            part.length ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : '',
        )
        .join(' ');
}

const MarketDetail = () => {
    const { indexKey } = useParams();
    const [indices, setIndices] = useState(null);
    const [rows, setRows] = useState([]);
    const [loadingRows, setLoadingRows] = useState(true);
    const [fetching5y, setFetching5y] = useState(false);
    const [error, setError] = useState('');
    const [range, setRange] = useState('YTD');

    const storedSym = indexKeyToStoredSymbol(indexKey);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch('/api/quotes/symbols', { credentials: 'include' });
                const data = await r.json();
                if (!r.ok) throw new Error(data.error || 'Failed to load index list');
                if (!cancelled) setIndices(data.indices || {});
            } catch (e) {
                if (!cancelled) setError(e.message);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const loadStoredRows = useCallback(async () => {
        setLoadingRows(true);
        setError('');
        try {
            const r = await fetch(`/api/markets?symbol=${encodeURIComponent(storedSym)}`, {
                credentials: 'include',
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || 'Failed to load market data');
            const list = Array.isArray(data) ? data : [];
            list.sort((a, b) => a.date.localeCompare(b.date));
            setRows(list);
        } catch (e) {
            setError(e.message);
            setRows([]);
        } finally {
            setLoadingRows(false);
        }
    }, [storedSym]);

    useEffect(() => {
        loadStoredRows();
    }, [loadStoredRows]);

    const handleFetch5y = async () => {
        setError('');
        setFetching5y(true);
        try {
            const r = await fetch('/api/markets/fetch/index/5y', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ name: indexKey }),
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || 'Fetch failed');
            await loadStoredRows();
        } catch (e) {
            setError(e.message);
        } finally {
            setFetching5y(false);
        }
    };

    const chartRows = useMemo(() => filterQuotesByPriceRange(rows, range), [rows, range]);

    // Table shows most recent trading day first.
    const tableRows = useMemo(() => [...rows].reverse(), [rows]);

    const fmtVol = (v) => (v >= 1_000_000 ? `${(v / 1_000_000).toFixed(2)}M` : Number(v).toLocaleString());

    const yahooSym = indices ? indices[indexKey] : null;
    const unknownIndex = indices && !(indexKey in indices);

    return (
        <div className="markets-page">
            <p className="markets-back-link-row">
                <Link to="/markets" className="markets-back-link">← All market indices</Link>
            </p>

            <div className="markets-detail-head">
                <h1 className="markets-detail-title">
                    {storedSym}
                    {yahooSym && <span className="markets-yahoo-ref">{yahooSym}</span>}
                </h1>
                <button
                    type="button"
                    className="markets-fetch-btn"
                    onClick={handleFetch5y}
                    disabled={fetching5y || loadingRows || unknownIndex}
                >
                    {fetching5y ? 'Refreshing…' : 'Refresh 5 years from Yahoo'}
                </button>
            </div>
            <p className="markets-lede">{formatIndexTitle(indexKey)} — daily closes stored in the database.</p>

            {error && <div className="alert-error">{error}</div>}
            {unknownIndex && (
                <div className="alert-error">Unknown market index &quot;{indexKey}&quot;.</div>
            )}

            {loadingRows && <p className="markets-muted">Loading stored rows…</p>}

            {!loadingRows && rows.length === 0 && !unknownIndex && (
                <div className="markets-empty">
                    <p>No rows in the database for this index yet.</p>
                    <p className="markets-empty-hint">Use &quot;Refresh 5 years from Yahoo&quot; above to load history.</p>
                </div>
            )}

            {!loadingRows && rows.length > 0 && (
                <>
                    <div className="stock-chart-block">
                        <span className="stock-chart-range-label" id="market-chart-range-label">
                            Chart range
                        </span>
                        <div
                            className="stock-chart-range-row"
                            role="group"
                            aria-labelledby="market-chart-range-label"
                        >
                            {MARKET_RANGE_PRESETS.map(p => (
                                <button
                                    key={p.id}
                                    type="button"
                                    className={`stock-chart-range-btn${range === p.id ? ' is-active' : ''}`}
                                    onClick={() => setRange(p.id)}
                                    disabled={loadingRows || fetching5y}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        {chartRows.length === 0 ? (
                            <p className="markets-muted">No rows in this range for the data currently loaded.</p>
                        ) : (
                            <StockPriceChart quotes={chartRows} />
                        )}
                    </div>

                    <div className="markets-table-wrap">
                        <p className="markets-row-count">{rows.length} trading days stored (most recent first)</p>
                        <table className="markets-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Open</th>
                                    <th>High</th>
                                    <th>Low</th>
                                    <th>Close</th>
                                    <th>Volume</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tableRows.map(row => (
                                    <tr key={row.date}>
                                        <td className="markets-td-date">{row.date}</td>
                                        <td>{row.opening?.toFixed?.(4) ?? row.opening}</td>
                                        <td>{row.high?.toFixed?.(4) ?? row.high}</td>
                                        <td>{row.low?.toFixed?.(4) ?? row.low}</td>
                                        <td>{row.closing?.toFixed?.(4) ?? row.closing}</td>
                                        <td className="markets-td-vol">{fmtVol(row.volume)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default MarketDetail;
